import urls from '@/lib/urls'
import omit from 'lodash/omit'
import type {
  LocationQuery,
  RouteLocationNormalized,
  RouteLocationNormalizedGeneric,
} from 'vue-router'
import { queryPathRegex } from '@/components/landing-page/helpers'
import {
  signinRedirect,
  hasValidUserSession,
  getUserId,
} from '@/services/identity-client'
import { CARD_ORIGINS } from '@/lib/enums-marketing'

/**
 * If we're coming from a trail page marketing link, we need to show content that may give
 * context and convince the user to create an account before sending them to identity
 */
export function showOffroadTrailsLandingPage(
  route: RouteLocationNormalizedGeneric
) {
  return route.query.utm_campaign === 'offroad-trail-pages'
}

export function showHuntLoginLandingPage() {
  return false
}

const parseUnsupportedParams = (queryParams: LocationQuery) => {
  return omit(queryParams, urls.getSupportedParams())
}

/**
 * The link preview page is only enabled in the following circumstances
 * 1. The route has a valid lat and lon query param
 *    a. Either as a query param
 *    b. Or as a path param /query/{lat},{lon}
 * 2. The route has a valid mode query param if the vertical is not hunt
 */
export function showLandingPagePreview(route: RouteLocationNormalized) {
  let hasValidMode = false
  if (!hasValidMode) {
    if (route.query.mode) {
      hasValidMode = true
    } else {
      const result = { ...route.query }

      if (route.query.after_login) {
        const afterLoginUrl = new URL(
          route.query.after_login as string,
          window.location.origin
        )

        for (const [key, value] of afterLoginUrl.searchParams.entries()) {
          result[key] = value
        }
      }

      hasValidMode = result.mode !== undefined
    }
  }

  let hasValidLatLon = route.fullPath.match(queryPathRegex) !== null
  if (!hasValidLatLon) {
    hasValidLatLon =
      route.query.lat !== undefined && route.query.lon !== undefined
  }

  return hasValidLatLon && hasValidMode
}

/**
 * For routes with a landing page preview, bypass it for users who already have an account.
 * - Valid session: redirect directly to the destination.
 * - Expired access token (userId exists): silently refresh the session via signinRedirect.
 * - No account: return undefined so the landing page renders.
 */
export async function bypassLandingForExistingUser(
  route: RouteLocationNormalized,
  defaultDestination: string
) {
  if (await hasValidUserSession()) {
    return (route.query?.after_login as string) ?? defaultDestination
  }

  const userId = await getUserId()
  if (userId) {
    return signinRedirect(
      {
        state: {
          after_login:
            (route.query?.after_login as string) ?? defaultDestination,
        },
      },
      {
        marketingProps: { origin: CARD_ORIGINS.SHARE_TOKEN_REFRESH },
      }
    )
  }
}

/** Old router login redirect logic */
export const redirectToLogin = (to: RouteLocationNormalizedGeneric) => {
  const parentRoute = to.matched[0]?.path || '/hunt'
  const supportedParams = urls.getSupportedParamsString()
  const unsupportedParams = parseUnsupportedParams(to.query)
  // only add the ? if params exist
  const after_login =
    supportedParams !== ''
      ? encodeURI(to.path + '?' + supportedParams)
      : encodeURI(to.path)

  return {
    path: `${parentRoute}/login`,
    query: { after_login, ...unsupportedParams },
    hash: to.hash,
  }
}
